'use client';

import { ReactElement } from 'react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { ParallaxProvider } from 'react-scroll-parallax';
import { config as faConfig } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { queryClient } from 'core/react-query/queryClient';
import {
  persister,
  PERSISTED_CACHE_MAX_AGE_MS,
  PERSISTED_CACHE_VERSION,
  shouldPersistQuery,
} from 'core/react-query/persistence';
import growthbook from 'integrations/growthbook/growthbook';
import { useGrowthBookInitialisation } from 'integrations/growthbook/hooks/useGrowthBookInitialisation';
import { SegmentAnalytics } from 'tags/SegmentTag/SegmentAnalytics';
import RouteProtector from 'hocs/RouteProtector';
import Modal from 'elements/Modal';
import SlideOver from 'elements/SlideOver';
import Notifications from 'elements/Notifications';
import useAuthenticationObserverApp from 'hooks/useAuthenticationObserverApp';
import SpaceAutoSelector from 'hocs/SpaceAutoSelector';
import { LoadMark, markLoad } from 'utils/PerformanceUtils';

faConfig.autoAddCss = false;

// App Router provider tree. Runs globally for both public and authenticated
// pages: RouteProtector gates protected routes, SpaceAutoSelector keeps the
// Space machine in sync with the URL, and the overlays (Modal, SlideOver,
// Notifications) mount once. Translations come from next-intl's
// NextIntlClientProvider (in the [locale] layout), not from here.
export function Providers({ children }: { children: ReactElement }) {
  useAuthenticationObserverApp();
  useGrowthBookInitialisation();

  return (
    // Restores the persisted part of the React Query cache from localStorage
    // before the first render, so a reload paints what the user last saw and
    // refreshes it in the background. Which queries are kept is decided in
    // core/react-query/persistence.
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: PERSISTED_CACHE_MAX_AGE_MS,
        buster: PERSISTED_CACHE_VERSION,
        dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
      }}
      onSuccess={() => markLoad(LoadMark.CacheRestored)}
    >
      <SpaceAutoSelector />
      <GrowthBookProvider growthbook={growthbook}>
        <SegmentAnalytics />
        <RouteProtector>
          <ParallaxProvider>
            {children}
            <Modal />
            <SlideOver />
            <Notifications />
          </ParallaxProvider>
        </RouteProtector>
      </GrowthBookProvider>
    </PersistQueryClientProvider>
  );
}
